if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        /*
         * 2026-07-29: buy-buttons.liquid renders "Add to Event", but
         * toggleSubmitButton() used to restore the label from
         * window.variantStrings.addToCart ("Add to cart"). <product-info>
         * calls toggleSubmitButton() on every variant change, so the button
         * read correctly on load and then flipped the moment a customer picked
         * a different package. Remembering the rendered label fixes that
         * without touching the locale files.
         */
        this.defaultSubmitText = this.submitButtonText ? this.submitButtonText.textContent.trim() : '';

        this.updating = false;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      /*
       * ------------------------------------------------------------------
       * Variant update state
       *
       * 2026-08-03: this replaces a click-queue implementation that was
       * adding the WRONG variant to the cart.
       *
       * The queue worked like this: while <product-info> re-fetched the
       * section after a variant change, the button was left clickable but
       * marked busy, a click was cancelled and remembered, and it was
       * replayed once the fetch resolved. The intent was to stop the first
       * click being swallowed.
       *
       * The flaw is that the hidden input[name="id"] that actually decides
       * what gets added is only rewritten by product-info's
       * updateVariantInputs() AFTER the fetch resolves. For the whole
       * "Updating..." window that input still holds the PREVIOUS variant
       * while the dropdown already shows the new one. Leaving the button
       * clickable in that window meant any submit that escaped the queue -
       * rather than being harmlessly swallowed as it was before - posted the
       * old variant ID. QA reproduced exactly that: select Ultimate Battle
       * Party ($199), click after roughly 100ms, cart received Battle Party
       * ($100).
       *
       * A silent wrong-price add is far worse than a click that does nothing,
       * so the queue and the replay are gone. The button is now genuinely
       * disabled for the duration of the update - a disabled button cannot be
       * submitted by click, keyboard or requestSubmit() - and is re-enabled
       * only by toggleSubmitButton(), which product-info calls after the
       * hidden input, price, availability and option UI have all been updated.
       *
       * Nothing here replays a click. The customer clicks once the button
       * says "Add to Event" again, and that click can only ever submit the
       * variant currently on screen.
       * ------------------------------------------------------------------
       */
      setUpdating(isUpdating = true) {
        if (!this.submitButton) return;

        if (!isUpdating) {
          this.clearUpdating();
          return;
        }

        this.updating = true;

        // Genuinely disabled, not merely styled as such.
        this.submitButton.setAttribute('disabled', 'disabled');
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.classList.add('loading');

        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        if (this.submitButtonText) this.submitButtonText.textContent = 'Updating…';
      }

      clearUpdating() {
        this.updating = false;

        if (!this.submitButton) return;

        this.submitButton.removeAttribute('aria-busy');
        this.submitButton.classList.remove('loading');

        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Belt and braces. A disabled button cannot submit, but this also
        // covers a programmatic requestSubmit() arriving mid-update.
        if (this.updating || this.submitButton.hasAttribute('disabled')) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      /*
       * Called by <product-info> once a variant change has fully landed - the
       * hidden variant input, price, SKU, inventory and option UI are all
       * updated by then. This is the only thing that re-enables the button.
       */
      toggleSubmitButton(disable = true, text) {
        this.clearUpdating();

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = this.defaultSubmitText || window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
